import React, { FC, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CONTACT } from '../data/constants';
import {
  Tag, Ribbon, Printer, Scan, Gem, Shirt, ShoppingCart,
  Factory, Package, HeartPulse, Pill, Phone, Wrench,
  Layers, Building2, ChevronDown, Menu, X, ChevronRight
} from 'lucide-react';

const Navbar: FC = () => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => {
    setIsOpen(false);
    setActiveDropdown(null);
  };

  useEffect(() => {
    closeMenu();
  }, [pathname]);

  const a = (p: string) => pathname === p ? 'active' : '';
  const prodActive = ['/products', '/barcode-labels', '/ttr-ribbons', '/printers', '/scanners', '/jewellery-labels', '/taffeta-labels', '/retail-pos'].some(p => pathname.startsWith(p)) ? 'active' : '';
  const indActive = pathname.startsWith('/industries') ? 'active' : '';

  const toggleDropdown = (name: string) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  return (
    <>
      <div className="topbar">
        <Package size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
        Pan-India Shipping &nbsp;·&nbsp;
        <Wrench size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
        AMC &amp; Repair &nbsp;·&nbsp;
        <Phone size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
        <a href={`tel:${CONTACT.phone1}`}>{CONTACT.phone1}</a>
      </div>
      <nav className="navbar">
        <div className="nav-inner">
          <Link to="/" className="nav-logo" onClick={closeMenu}>
            <div className="nav-logo-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 4h4v16H2V4zm5 0h2v16H7V4zm3 0h1v16h-1V4zm2 0h2v16h-2V4zm3 0h1v16h-1V4zm2 0h3v16h-3V4z" />
              </svg>
            </div>
            <div>
              <div className="nav-brand">DevBhoomi Labels</div>
              <div className="nav-tag">devbhoomilabels.com</div>
            </div>
          </Link>

          {/* Desktop Links */}
          <ul className="nav-links">
            <li><Link to="/" className={a('/')}>Home</Link></li>
            <li><Link to="/company" className={a('/company')}>Company</Link></li>
            <li className="dropdown">
              <Link to="/products" className={prodActive}>
                Barcode Solutions <ChevronDown size={13} style={{ display: 'inline', verticalAlign: 'middle' }} />
              </Link>
              <div className="dropdown-menu">
                <Link to="/barcode-labels"><Tag size={15} />Barcode Labels</Link>
                <Link to="/ttr-ribbons"><Ribbon size={15} />Thermal Transfer Ribbons</Link>
                <Link to="/printers"><Printer size={15} />Barcode Printers</Link>
                <Link to="/scanners"><Scan size={15} />Barcode Scanners</Link>
                <div className="dropdown-sep" />
                <Link to="/jewellery-labels"><Gem size={15} />Jewellery Labels</Link>
                <Link to="/taffeta-labels"><Shirt size={15} />Taffeta Labels</Link>
                <Link to="/retail-pos"><ShoppingCart size={15} />Retail POS / Billing</Link>
              </div>
            </li>
            <li className="dropdown">
              <Link to="/industries" className={indActive}>
                Industries <ChevronDown size={13} style={{ display: 'inline', verticalAlign: 'middle' }} />
              </Link>
              <div className="dropdown-menu">
                <Link to="/industries/manufacturing"><Factory size={15} />Manufacturing</Link>
                <Link to="/industries/retail"><ShoppingCart size={15} />Retail Industry</Link>
                <Link to="/industries/logistics"><Package size={15} />Logistics &amp; Supply Chain</Link>
                <Link to="/industries/healthcare"><HeartPulse size={15} />Healthcare</Link>
                <Link to="/industries/food-beverage"><Layers size={15} />Food &amp; Beverage</Link>
                <Link to="/industries/pharmaceutical"><Pill size={15} />Pharmaceutical</Link>
                <div className="dropdown-sep" />
                <Link to="/industries/ecommerce"><ShoppingCart size={15} />E-commerce</Link>
                <Link to="/industries/jewellery"><Gem size={15} />Jewellery Retail</Link>
                <Link to="/industries/garments"><Shirt size={15} />Garments &amp; Textiles</Link>
                <Link to="/industries"><Building2 size={15} />View All Industries</Link>
              </div>
            </li>
            <li className="dropdown">
              <Link to="/services" className={a('/services')}>
                Services <ChevronDown size={13} style={{ display: 'inline', verticalAlign: 'middle' }} />
              </Link>
              <div className="dropdown-menu">
                <Link to="/services#amc"><Wrench size={15} />Repair &amp; Maintenance (AMC)</Link>
                <Link to="/services#support"><Phone size={15} />Technical Support</Link>
              </div>
            </li>
            <li><Link to="/software" className={a('/software')}>Software</Link></li>
            <li><Link to="/branches" className={a('/branches')}>Branches</Link></li>
            <li><Link to="/contact" className={`nav-cta ${a('/contact')}`}>Get a Quote</Link></li>
          </ul>

          {/* Hamburger Icon */}
          <button className="hamburger" onClick={toggleMenu} aria-label="Toggle Menu">
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`nav-menu-mobile ${isOpen ? 'active' : ''}`}>
          <ul className="mobile-links">
            <li><Link to="/" className={a('/')} onClick={closeMenu}>Home</Link></li>
            <li><Link to="/company" className={a('/company')} onClick={closeMenu}>Company</Link></li>
            
            <li>
              <button className={`mobile-dropdown-btn ${prodActive ? 'active' : ''}`} onClick={() => toggleDropdown('products')}>
                Barcode Solutions <ChevronDown size={14} style={{ transform: activeDropdown === 'products' ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
              </button>
              <div className={`mobile-dropdown-content ${activeDropdown === 'products' ? 'active' : ''}`}>
                <Link to="/barcode-labels" onClick={closeMenu}><Tag size={14} /> Barcode Labels</Link>
                <Link to="/ttr-ribbons" onClick={closeMenu}><Ribbon size={14} /> TTR Ribbons</Link>
                <Link to="/printers" onClick={closeMenu}><Printer size={14} /> Barcode Printers</Link>
                <Link to="/scanners" onClick={closeMenu}><Scan size={14} /> Barcode Scanners</Link>
                <Link to="/jewellery-labels" onClick={closeMenu}><Gem size={14} /> Jewellery Labels</Link>
                <Link to="/taffeta-labels" onClick={closeMenu}><Shirt size={14} /> Taffeta Labels</Link>
                <Link to="/retail-pos" onClick={closeMenu}><ShoppingCart size={14} /> Retail POS</Link>
              </div>
            </li>

            <li>
              <button className={`mobile-dropdown-btn ${indActive ? 'active' : ''}`} onClick={() => toggleDropdown('industries')}>
                Industries <ChevronDown size={14} style={{ transform: activeDropdown === 'industries' ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
              </button>
              <div className={`mobile-dropdown-content ${activeDropdown === 'industries' ? 'active' : ''}`}>
                <Link to="/industries/manufacturing" onClick={closeMenu}><Factory size={14} /> Manufacturing</Link>
                <Link to="/industries/retail" onClick={closeMenu}><ShoppingCart size={14} /> Retail</Link>
                <Link to="/industries/logistics" onClick={closeMenu}><Package size={14} /> Logistics</Link>
                <Link to="/industries/healthcare" onClick={closeMenu}><HeartPulse size={14} /> Healthcare</Link>
                <Link to="/industries" onClick={closeMenu}><Building2 size={14} /> View All</Link>
              </div>
            </li>

            <li>
              <button className="mobile-dropdown-btn" onClick={() => toggleDropdown('services')}>
                Services <ChevronDown size={14} style={{ transform: activeDropdown === 'services' ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
              </button>
              <div className={`mobile-dropdown-content ${activeDropdown === 'services' ? 'active' : ''}`}>
                <Link to="/services#amc" onClick={closeMenu}><Wrench size={14} /> AMC / Repair</Link>
                <Link to="/services#support" onClick={closeMenu}><Phone size={14} /> Support</Link>
              </div>
            </li>

            <li><Link to="/software" className={a('/software')} onClick={closeMenu}>Software</Link></li>
            <li><Link to="/branches" className={a('/branches')} onClick={closeMenu}>Branches</Link></li>
            <li><Link to="/contact" className="nav-cta" onClick={closeMenu} style={{ background: 'var(--accent)', justifyContent: 'center', marginTop: 10 }}>Get a Quote <ChevronRight size={16} /></Link></li>
          </ul>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
